// The getting-started checklist.
//
// This rendered for nobody until 2026-09-21. It read `user` and
// `subscriberInfo` from AuthContext, keys AuthContext has never had, so the
// load effect always took its "not signed in" branch and set no steps, and
// `completeStep` returned early on the same check. A new trial workspace
// therefore landed on an empty task list with nothing saying what to do next.
//
// Switching the wiring on was not enough by itself, because no step had ever
// been recorded as done for anyone. Every established workspace would have
// been greeted with a 0% "let's get you set up" card. So each required step is
// now read from the workspace itself rather than from a record of clicks:
// "Build a task template" is done when a template exists, however it got there.
// A workspace that is already running reads as complete and never sees the
// card, and one stamp on the user's onboarding document stops it being
// checked again.
//
// The steps follow the getting-started guide handed to trial subscribers
// (docs/getting-started/content.py), including the one step the old list left
// out entirely: installing the app on a phone.
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import useClaimsReadySnapshot from '../hooks/useClaimsReadySnapshot';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

// `derivedFrom` names the setup flag that completes the step. A step without
// one (the optional invite) is completed by the page that does it.
//
// `spotlight` is the `data-tour` element "Show me" highlights once `path` has
// loaded. It must exist on that page.
//
// The order is "try it yourself, then add your people". A new trial comes with
// a throwaway Demo operator (operator_service.create_demo_operator), so the
// admin can build a template, sign in on a phone as that operator and see a
// job arrive before adding anyone real. The template comes first because the
const STEP = {
  create_template: {
    id: 'create_template',
    title: 'Build a task template',
    description:
      'The form filled in on the phone: what gets answered and which photos are taken. Start from the recipe closest to your work.',
    path: '/settings/task-templates',
    spotlight: 'new-template',
    derivedFrom: 'hasTemplate',
  },
  install_app: {
    id: 'install_app',
    title: 'Install TrakLog on a phone',
    description:
      'Install the TrakLog app on an Android phone and enter your workspace code.',
    path: '/settings/operators',
    spotlight: 'workspace-code',
    derivedFrom: 'hasSignedInPhone',
    installsApp: true,
  },
  first_task: {
    id: 'first_task',
    title: 'Record your first job',
    description: 'Complete a task on the phone. It appears on the Tasks page as soon as it uploads.',
    path: '/tasks',
    spotlight: 'task-list',
    derivedFrom: 'hasTask',
  },
  add_operator: {
    id: 'add_operator',
    title: 'Add your own operators',
    description:
      'Operators are the people in the field. They sign in on the phone with a PIN, not an email address. The trial\'s Demo operator also works for a first try; its PIN is on the Operators page.',
    path: '/settings/operators',
    spotlight: 'add-operator',
    derivedFrom: 'hasOwnOperator',
  },
  invite_user: {
    id: 'invite_user',
    title: 'Invite your colleagues',
    description: 'Add managers or other admins to review and manage the work with you.',
    path: '/settings/users',
    optional: true,
  },
};

// The admin created the trial and is the first person inside it, so theirs is
// the full list, in the order a new customer works: the people, the phone,
// a first job, then the form (a job can be recorded without a template). A
// manager can do everything but invite users. A user cannot add operators or
// templates, so they get the two steps they can do themselves: get the app,
// and record a job with an operator an admin set up.
export const STEPS_BY_ROLE = {
  admin: [STEP.add_operator, STEP.install_app, STEP.first_task, STEP.create_template, STEP.invite_user],
  manager: [STEP.add_operator, STEP.install_app, STEP.first_task, STEP.create_template],
  user: [
    {
      ...STEP.install_app,
      description:
        'Install the TrakLog app on an Android phone, enter your workspace code, and sign in with the operator name and PIN your admin gives you.',
    },
    STEP.first_task,
  ],
};

// Kept for callers that only care about the admin list.
export const ADMIN_STEPS = STEPS_BY_ROLE.admin;

const stepsForRole = (role) => STEPS_BY_ROLE[role] || [];

// Managers and admins can read operator PINs (operator_credentials is
// `hasElevatedSubscriberAccess` in firestore.rules); users cannot.
const canReadPins = (role) => role === 'admin' || role === 'manager';

// How many operators and templates to look at. Enough to answer "is there
// one?" for any trial, and for a large workspace the first page already holds
// an operator who has signed in. Reading every operator on each page change
// would cost a busy workspace hundreds of reads for a card it will never show.
const SCAN_LIMIT = 50;

/**
 * Read what the workspace has actually done.
 *
 * Resolves to the flags the steps are derived from, plus the operator the
 * phone step tells them to sign in as (their own first operator, or the
 * trial's Demo operator until they have one) and its PIN when the caller may
 * read it. Rejects if any read fails, and the caller then hides the
 * checklist: a guess that says "not set up" would show an established
 * workspace a 0% card.
 */
export async function readSetupState(subscriberId, { withPin = false } = {}) {
  const [operators, templates, tasks] = await Promise.all([
    getDocs(query(collection(db, 'subscribers', subscriberId, 'operators'), limit(SCAN_LIMIT))),
    getDocs(query(collection(db, 'subscribers', subscriberId, 'formTemplates'), limit(SCAN_LIMIT))),
    getDocs(query(collection(db, 'tasks'), where('subscriber_id', '==', subscriberId), limit(1))),
  ]);

  const liveOperators = operators.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((o) => o.deleted !== true);
  const demo = liveOperators.find((o) => o.is_demo === true);
  // Adding an operator is the first step, so once they have one the phone
  // step names it rather than Demo. Firestore returns them in id order, which
  // is as good a "first" as any.
  const signIn = liveOperators.find((o) => o.is_demo !== true) || demo || null;

  // The PIN is not on the operator document, which every member can read; it
  // is in operator_credentials. A failure here only costs the hint, so it is
  // not allowed to hide the checklist.
  let signInPin = null;
  if (withPin && signIn) {
    try {
      const cred = await getDoc(doc(db, 'subscribers', subscriberId, 'operator_credentials', signIn.id));
      signInPin = cred.exists() ? cred.data().pin || null : null;
    } catch (err) {
      signInPin = null;
    }
  }

  return {
    // The trial's Demo operator is for trying the app, not one of their people.
    hasOwnOperator: liveOperators.some((o) => o.is_demo !== true),
    // Written by the operator sign-in function on the phone
    // (functions/functions/auth_handlers.py::_login_bookkeeping). An operator
    // created on the web has all three unset until a phone signs in as them.
    // Signing in as the Demo operator counts: that is the app installed.
    hasSignedInPhone: liveOperators.some((o) => o.app_version || o.last_seen || o.last_login),
    hasTemplate: templates.docs.some((d) => (d.data() || {}).deleted !== true),
    hasTask: !tasks.empty,
    signInOperatorName: signIn ? signIn.name : null,
    signInPin,
  };
}

export const OnboardingProvider = ({ children }) => {
  const { currentUser: user, subscriberId, subscriberRole } = useAuth();

  // What the user's onboarding document says.
  const [record, setRecord] = useState({
    loaded: false,
    dismissed: false,
    completedSteps: [],
    setupComplete: false,
  });
  // What the workspace says: null while unknown, false if it could not be read.
  const [setup, setSetup] = useState(null);
  const readingRef = useRef(false);

  const steps = useMemo(
    () => (user?.uid && subscriberId ? stepsForRole(subscriberRole) : []),
    [user?.uid, subscriberId, subscriberRole]
  );

  const onboardingDocRef = useCallback(
    () => doc(db, 'subscribers', subscriberId, 'user_onboarding', user.uid),
    [subscriberId, user?.uid]
  );

  // The user's onboarding document.
  useEffect(() => {
    setSetup(null);
    if (!user?.uid || !subscriberId) {
      setRecord({ loaded: true, dismissed: false, completedSteps: [], setupComplete: false });
      return;
    }
    // "Not loaded" shows nothing until the document has been read.
    setRecord((prev) => ({ ...prev, loaded: false }));
  }, [user?.uid, subscriberId]);

  // Mounted above login, so this would run the moment a workspace is chosen,
  // before the token carries it. A listener the rules refuse stays dead and
  // its error hides the checklist, so it waits for the claim and listens again
  // after a refusal (useClaimsReadySnapshot).
  const { status: recordStatus } = useClaimsReadySnapshot(
    (sid) => (user?.uid ? doc(db, 'subscribers', sid, 'user_onboarding', user.uid) : null),
    {
      subscriberId: subscriberId || null,
      label: 'Onboarding state',
      onNext: (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setRecord({
          loaded: true,
          dismissed: data.dismissed === true,
          completedSteps: Array.isArray(data.completedSteps) ? data.completedSteps : [],
          setupComplete: data.setupComplete === true,
        });
      },
      onError: (error) => {
        console.error('Error loading onboarding state:', error);
        // Unknown is treated as "do not show": see readSetupState.
        setRecord({ loaded: true, dismissed: true, completedSteps: [], setupComplete: false });
      },
    },
    [user?.uid || null]
  );

  // A session that can never read the document (a customer-portal token)
  // gets no checklist rather than one waiting forever.
  useEffect(() => {
    if (recordStatus === 'idle' && user?.uid && subscriberId) {
      setRecord({ loaded: true, dismissed: true, completedSteps: [], setupComplete: false });
    }
  }, [recordStatus, user?.uid, subscriberId]);

  // Still read after the card is closed: Settings, User Guide mirrors the
  // checklist, and without the read every derived step would show unticked
  // there. Only a finished setup stops the reads.
  const needsSetupRead = record.loaded && !record.setupComplete && steps.length > 0;

  /** Re-read the workspace. The checklist calls this on every page change. */
  const refreshSetup = useCallback(async () => {
    if (!needsSetupRead || !subscriberId || readingRef.current) return;
    readingRef.current = true;
    try {
      setSetup(await readSetupState(subscriberId, { withPin: canReadPins(subscriberRole) }));
    } catch (error) {
      console.error('Error reading workspace setup:', error);
      setSetup(false);
    } finally {
      readingRef.current = false;
    }
  }, [needsSetupRead, subscriberId, subscriberRole]);

  useEffect(() => {
    refreshSetup();
  }, [refreshSetup]);

  const isStepCompleted = useCallback(
    (stepId) => {
      if (record.setupComplete) return true;
      const step = steps.find((s) => s.id === stepId);
      if (step?.derivedFrom) return Boolean(setup && setup[step.derivedFrom]);
      return record.completedSteps.includes(stepId);
    },
    [record.setupComplete, record.completedSteps, setup, steps]
  );

  const requiredSteps = steps.filter((s) => !s.optional);
  const completedRequired = requiredSteps.filter((s) => isStepCompleted(s.id)).length;
  const progress = requiredSteps.length > 0
    ? Math.round((completedRequired / requiredSteps.length) * 100)
    : 0;
  const completedSteps = steps.filter((s) => isStepCompleted(s.id)).map((s) => s.id);

  const loading = !record.loaded || (needsSetupRead && setup === null);

  // Shown only while the workspace is demonstrably not set up. Loading,
  // unreadable, dismissed and complete all mean hidden.
  const shouldShowOnboarding =
    !loading &&
    !record.dismissed &&
    !record.setupComplete &&
    Boolean(setup) &&
    requiredSteps.length > 0 &&
    progress < 100;

  // Stamp a finished setup once, so the workspace is not read again.
  useEffect(() => {
    if (!needsSetupRead || !setup || requiredSteps.length === 0 || progress < 100) return;
    setDoc(
      onboardingDocRef(),
      {
        setupComplete: true,
        setupCompletedAt: new Date().toISOString(),
        userId: user.uid,
        userEmail: user.email || null,
      },
      { merge: true }
    ).catch((error) => console.error('Error recording completed setup:', error));
  }, [needsSetupRead, setup, progress, requiredSteps.length, onboardingDocRef, user?.uid, user?.email]);

  // Mark a step done by hand. Only the optional invite step still relies on
  // this; the required ones are read from the workspace, so their callers are
  // now harmless rather than the only record there was.
  const completeStep = useCallback(async (stepId) => {
    if (!user?.uid || !subscriberId) return;
    try {
      await setDoc(
        onboardingDocRef(),
        {
          completedSteps: [...new Set([...record.completedSteps, stepId])],
          lastUpdated: new Date().toISOString(),
          userId: user.uid,
          userEmail: user.email || null,
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error completing onboarding step:', error);
    }
  }, [user?.uid, user?.email, subscriberId, record.completedSteps, onboardingDocRef]);

  const dismissOnboarding = useCallback(async () => {
    if (!user?.uid || !subscriberId) return;
    try {
      await setDoc(
        onboardingDocRef(),
        {
          dismissed: true,
          dismissedAt: new Date().toISOString(),
          userId: user.uid,
          userEmail: user.email || null,
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error dismissing onboarding:', error);
    }
  }, [user?.uid, user?.email, subscriberId, onboardingDocRef]);

  // Bring the checklist back. It still hides itself if the workspace is set up.
  const resetOnboarding = useCallback(async () => {
    if (!user?.uid || !subscriberId) return;
    try {
      await setDoc(
        onboardingDocRef(),
        {
          dismissed: false,
          setupComplete: false,
          resetAt: new Date().toISOString(),
          userId: user.uid,
          userEmail: user.email || null,
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error resetting onboarding:', error);
    }
  }, [user?.uid, user?.email, subscriberId, onboardingDocRef]);

  const getNextStep = useCallback(
    () =>
      requiredSteps.find((s) => !isStepCompleted(s.id)) ||
      steps.find((s) => !isStepCompleted(s.id)),
    [requiredSteps, steps, isStepCompleted]
  );

  const value = {
    loading,
    dismissed: record.dismissed,
    // The workspace facts, for hints such as the Demo operator's PIN.
    setup: setup || null,
    steps,
    completedSteps,
    progress,
    shouldShowOnboarding,
    completeStep,
    dismissOnboarding,
    resetOnboarding,
    refreshSetup,
    getNextStep,
    isStepCompleted,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export default OnboardingContext;
